import React, { useEffect, useState, useMemo } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { Link } from '@mui/material'
import { viewId, titleName, restApiKey, masterClassification } from '../../../_constants/Code'
import { errorLog } from '../../../common/logger/Logger'
import { ChrofyCustomSelect } from '../../common/ChrofyCustomSelect'
import Checkbox from '@mui/material/Checkbox'
import CheckboxRountedIcon from '@mui/icons-material/CheckBoxRounded'
import Forbidden from '../../../_components/Forbidden'
import { useSelector } from 'react-redux'
import { useModal } from '../../../_components/Modal'
import { fetchMasterData, fetchCompanyData, updateCompanyInfoApi, fetchIndustrySectorData } from './extra/services'
import { listingClassification, reportingCompanyCategory, marketSegment } from './extra/pulldownOptions'
import '../../../assets/css/common.css'
import { getMessage } from '../../../common/utils/MessageUtils'
import RelatedDashboard from '../parts/RelatedDashboard'

/**
 * Master.RelatedDashboard を RelatedDashboard 用の string[] に揃える（Dynamo 表現の { S: "x" } も許容）。 RelatedDashboard はマウント時のみ
 * props を取り込むため、データ変化時は key で再マウントする。
 *
 * @param {unknown} value
 * @returns {string[]}
 */
function normalizeRelatedDashboardIds(value) {
  if (!value || !Array.isArray(value)) return []
  return value
    .map((item) => {
      if (typeof item === 'string') return item.trim()
      if (item != null && typeof item === 'object' && item.S != null) return String(item.S).trim()
      return ''
    })
    .filter(Boolean)
}

/**
 * 入力禁止文字が含まれているかチェック
 *
 * @param {string} value 入力値
 * @param {string[]} disallowedChars 禁止文字配列
 * @returns {boolean}
 */
const checkIfCharsExist = (value, disallowedChars) => {
  if (!value) return false
  return disallowedChars.some((char) => value.includes(char))
}

/**
 * DynamoDB には業種名を保存するが、プルダウンは key を value に使う。 保存値が key または name のいずれでも select 用の key に正規化する。
 *
 * @param {string} stored Company.IndustrySector
 * @param {{ key: string; name: string; list?: Array }[]} industryList
 * @returns {string}
 */
const resolveIndustryKeyForSelect = (stored, industryList) => {
  if (!stored || !industryList?.length) return (stored || '').trim()
  const trimmed = String(stored).trim()
  if (!trimmed) return ''

  for (const item of industryList) {
    if (item.key === trimmed) return trimmed
    if (item.list?.length) {
      for (const sub of item.list) {
        if (sub.key === trimmed) return trimmed
      }
    }
  }
  for (const item of industryList) {
    if (item.name === trimmed) return item.key
    if (item.list?.length) {
      for (const sub of item.list) {
        if (sub.name === trimmed) return sub.key
      }
    }
  }
  return trimmed
}

/**
 * 業種 key から名称を返す（API 保存用）。見つからない場合は空文字。
 *
 * @param {string} key
 * @param {{ key: string; name: string; list?: Array }[]} industryList
 * @returns {string}
 */
const getIndustryNameByKey = (key, industryList) => {
  if (!key || !industryList?.length) return ''
  const trimmed = String(key).trim()
  for (const item of industryList) {
    if (item.key === trimmed) return item.name ?? ''
    if (item.list?.length) {
      for (const sub of item.list) {
        if (sub.key === trimmed) return sub.name ?? ''
      }
    }
  }
  return ''
}

//STATE 定義
const CompanyInfoContent = (props) => {
  const [isLoaded, setIsLoaded] = useState(false) // 画面表示制御
  const [hasCompanyData, setHasCompanyData] = useState(true)
  const [industryOptionsDynamic, setIndustryOptionsDynamic] = useState([])
  // マスタ・会社情報
  const [master, setMaster] = useState(null)
  const [companyData, setCompanyData] = useState()
  // 入力値
  const [companyName, setCompanyName] = useState()
  const [accountingPeriod, setAccountingPeriod] = useState('')
  const [industrySector, setIndustrySector] = useState('')
  const [stockCode, setstockCode] = useState('')
  // プルダウン選択値
  const [listingClassificationType, setlistingClassificationType] = useState('')
  const listingClassificationOptions = listingClassification.map((opt) => ({
    value: opt.value,
    label: opt.label
  }))
  const [reportingCompanyCategoryType, setreportingCompanyCategoryType] = useState('')
  const reportingCompanyCategoryOptions = reportingCompanyCategory.map((opt) => ({
    value: opt.value,
    label: opt.label
  }))
  const [marketSegmentType, setmarketSegmentType] = useState('')
  const marketSegmentOptions = marketSegment.map((opt) => ({
    value: opt.value,
    label: opt.label
  }))
  const [IsCoreCompany, setIsCoreCompany] = useState(false)
  const handleIsCoreCompanyChange = () => {
    setIsCoreCompany(!IsCoreCompany)
  }

  /**
   * 業種プルダウン表示用オプションを生成する
   *
   * ・親業種：通常表示（NN） ・子業種：上位業種との階層関係が分かるように 表示名の先頭にインデント（空白）を付与して表示
   *
   * ※ 空白は半角・全角を問わず、階層が視覚的に分かれば可
   *
   * @param {Array} industryList 業種マスタ（階層構造）
   * @returns {Array} プルダウン用オプション
   */
  const generateIndustryOptions = (industryList) => {
    const options = []
    industryList.forEach((item) => {
      if (item.list && item.list.length > 0) {
        // 親業種
        options.push({
          value: item.key,
          label: `${item.key} ${item.name}`
        })
        // 子業種（NN-NN形式）
        item.list.forEach((subItem) => {
          options.push({
            value: subItem.key,
            label: '\u00A0\u00A0\u00A0' + `${subItem.key} ${subItem.name}`
          })
        })
      } else {
        options.push({
          value: item.key,
          label: `${item.key} ${item.name}`
        })
      }
    })
    return options
  }

  // エラー表示
  const [inputError, setInputError] = useState({ errorMessages: [] })
  const [errorCompanyName, setErrorCompanyName] = useState('')
  const [errorStockCode, setErrorStockCode] = useState('')
  const [hasInputError, setHasInputError] = useState(false)

  // タイトル設定
  useEffect(() => {
    document.title = props.pageTitle
    window.scrollTo(0, 0)
    init()
  }, [])

  /** DynamoDB 取得処理 */
  // /**
  //  * 会社情報取得関数
  //  *
  //  * @returns {Promise<Object>} 会社情報
  //  */

  const init = async () => {
    const industryList = await fetchIndustrySectorData(props.corporationNumber, props.userId)
    setIndustryOptionsDynamic(industryList)
    const masterData = await fetchMasterData(props.corporationNumber, props.userId)
    const companyInfo = await fetchCompanyData(props.corporationNumber, props.userId)
    setMaster(masterData)
    setCompanyData(companyInfo)
    if (companyInfo) {
      setCompanyName(companyInfo.CompName || '')
      setAccountingPeriod(companyInfo.AccountingPeriod || '')
      setIndustrySector(resolveIndustryKeyForSelect(companyInfo.IndustrySector || '', industryList))
      setlistingClassificationType(companyInfo.ListingClassification || '')
      setreportingCompanyCategoryType(companyInfo.ReportingCompanyCategory || '')
      setmarketSegmentType(companyInfo.MarketSegment || '')
      setstockCode(companyInfo.StockCode || '')
      setIsCoreCompany(companyInfo.IsCoreCompany || false)
      setHasCompanyData(true)
    } else {
      setHasCompanyData(false)
    }
    setIsLoaded(true)
  }
  const optionsForSelect = useMemo(() => {
    return generateIndustryOptions(industryOptionsDynamic)
  }, [industryOptionsDynamic])

  /**
   * 会社情報入力チェック
   *
   * @returns {boolean} True: OK / false: NG
   */
  const validateCompanyInfo = () => {
    let hasError = false

    // エラー初期化
    setErrorCompanyName('')
    setErrorStockCode('')
    setHasInputError(false)

    // ===== 会社名: 必須チェック =====
    if (!companyName || companyName.trim() === '') {
      setErrorCompanyName(getMessage('E010001', '会社名'))
      hasError = true
    } else {
      const disallowed = [',', '\\', '"']
      if (checkIfCharsExist(companyName, disallowed)) {
        setErrorCompanyName(getMessage('E010016', disallowed.join(' ')))
        hasError = true
      }
    }

    // 証券コードフォーマットチェック（任意）
    if (stockCode && stockCode !== '') {
      if (!/^\d{4}$/.test(stockCode)) {
        setErrorStockCode(getMessage('E011016', '証券コード', '4'))
        hasError = true
      }
    }

    setHasInputError(hasError)
    return !hasError
  }
  /** 会社情報更新処理 ・入力チェック ・確認モーダル表示 ・API実行 */
  const updateCompanyInfo = async (body) => {
    setInputError({ errorMessages: [] })

    if (!validateCompanyInfo()) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    const title = 'マスター設定値登録の確認'
    const description = (
      <>
        <div className="text-center">設定値を登録してダッシュボードに反映します。よろしいですか？</div>
        <div className="pt-5 text-center">
          ※ダッシュボードへの反映には5分～10分程度かかります
          <br />
          マスター管理画面のステータスから反映状況を確認してください
        </div>
      </>
    )

    const buttons = [
      {
        name: '実行',
        className: 'footer-btn button_base',
        click: async () => {
          try {
            setInputError({ errorMessages: [] })

            const industryNameForDb =
              getIndustryNameByKey(industrySector, industryOptionsDynamic) || (industrySector ?? '')
            const requestBody = {
              CorporationNumber: props.corporationNumber ?? '',
              CompName: (companyName ?? '').trim().replace(/,/g, ''),
              IndustrySector: industryNameForDb.replace(/,/g, ''),
              ListingClassification: (listingClassificationType ?? '').replace(/,/g, ''),
              ReportingCompanyCategory: (reportingCompanyCategoryType ?? '').replace(/,/g, ''),
              MarketSegment: (marketSegmentType ?? '').replace(/,/g, ''),
              StockCode: (stockCode ?? '').replace(/,/g, ''),
              IsCoreCompany: !!IsCoreCompany
            }
            const res = await updateCompanyInfoApi(props.corporationNumber, requestBody, props.userId)

            if (res.message && (res.message.indexOf('成功') !== -1 || res.message.indexOf('Success') !== -1)) {
              props.navigate('/master/masterList', { state: { message: 'データ反映を開始しました。' } })
            } else {
              setInputError({ errorMessages: ['更新処理でエラーが発生しました。'] })
              window.scrollTo(0, 0)
            }
          } catch (error) {
            errorLog('E000001', props.userId, viewId.companyInfo, 'updateCompanyInfo', error)
          }
        }
      }
    ]

    props.showModal({
      title,
      description,
      isWide: true,
      button: buttons
    })
  }
  if (!isLoaded) return null

  return (
    <div className="company-info-container">
      <main className="page-container">
        <section className="page-content">
          <div className="centered-container">
            <div className="bg main">
              <div className="back-upper-level py-8 pl-8 border-b border-blue2-lighten-20">
                <Link color="inherit" variant="button" underline="none" component={RouterLink} to="/master/masterList">
                  <span className="back-upper-level__link link--text hover:lighten-70 flex items-center">
                    <span className="icon-arrow-circle icon-32px mr-4 rotate-180 relative after:absolute after:bg-white after:w-full after:h-full after:rounded-full after:left-0 after:-z-10"></span>
                    <span className="text-h5 font-bold">{titleName.masterList}画面に戻る</span>
                  </span>
                </Link>
              </div>
            </div>
            <header className="page-header flex justify-center py-1">
              <h1 className="page-header__name text-h1 grey--text py-8">
                {masterClassification[master?.Classification]
                  ? masterClassification[master.Classification].name
                  : '会社関連マスター'}
              </h1>
            </header>
            <div className="page-body">
              {/* 入力エラーメッセージ表示 */}
              {hasInputError && <span className="text-red-600">入力内容に誤りがあります。</span>}
              {!hasCompanyData && <span className="text-red-600 block mt-2">会社情報が取得できませんでした。</span>}
              {/* ここに表示したい内容を記述 */}
              <div className="box-xy-bordered rounded-t-3xl rounded-b-2xl pt-10 pb-8 px-16 border-blue1">
                <h2 className="box-bordered-heading blue1 white--text text-h3 font-medium pt-2.5 pb-3 px-14 -mt-10 -mx-16 rounded-t-2xl">
                  会社情報設定
                </h2>

                <p className="mt-[20px]">会社情報を設定してください。</p>
                <div className="text-body-2-medium grow mt-6">■ 基本情報</div>

                <div className="setting-table">
                  <table className="setting-table__table w-full form-table mt-4" style={{ borderCollapse: 'collapse' }}>
                    <colgroup>
                      <col style={{ width: '300px' }} />
                      <col />
                    </colgroup>

                    <tbody className="text-base">
                      <tr className="border-b border-[#ddd]">
                        <th className="p-0 min-h-[20px] bg-[#f5f5f5]">
                          <div className="h-full flex items-center">
                            <label className="px-3 py-3 w-[200px] inline-flex items-center gap-2 text-base text-[#7476A6]">
                              国コード
                            </label>
                          </div>
                        </th>

                        <td className="p-0">
                          <div className="h-full flex items-center px-3 py-3 w-full ">
                            <span className="text-left text-base">{companyData?.CountryCode || ''}</span>
                          </div>
                        </td>
                      </tr>

                      {/* 法人番号の行 */}
                      <tr className="border-b border-[#ddd]">
                        <th className="p-0 min-h-[20px] bg-[#f5f5f5]">
                          <div className="h-full flex items-center">
                            <label className="px-3 py-3 w-[200px] inline-flex items-center gap-2 text-base text-[#7476A6]">
                              法人番号
                            </label>
                          </div>
                        </th>

                        <td className="p-0">
                          <div className="h-full flex items-center px-3 py-3 w-full ">
                            <span className="text-left text-base">{companyData?.CorporationNumber || ''}</span>
                          </div>
                        </td>
                      </tr>

                      {/* 会社名の行 */}
                      <tr className="border-b border-[#ddd]">
                        <th className="p-0 min-h-[20px] bg-[#f5f5f5]">
                          <div className="h-full flex items-center">
                            <label className="px-3 py-3 w-[200px] inline-flex items-center gap-2 text-base text-[#7476A6]">
                              会社名<span className="red--text">*</span>
                            </label>
                          </div>
                        </th>

                        <td className="p-0">
                          <div className="h-full flex flex-col px-3 py-3 w-full ">
                            <input
                              type="text"
                              className={`w-full py-3 px-3 rounded-xl ${errorCompanyName ? 'is-error' : 'border border-gray-300'}`}
                              value={companyName}
                              onChange={(e) => setCompanyName(e.target.value)}
                              required
                              placeholder="会社名を入力してください"
                            />
                            {errorCompanyName && (
                              <div className="text-red-600 mt-1 text-left self-start">{errorCompanyName}</div>
                            )}
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  <div className="text-body-2-medium grow mt-6">■ 属性情報</div>

                  <div className="setting-table">
                    <table
                      className="setting-table__table w-full form-table mt-4 table-fixed"
                      style={{ borderCollapse: 'collapse' }}
                    >
                      <colgroup>
                        <col style={{ width: '300px' }} />
                        <col />
                      </colgroup>

                      <tbody className="text-base">
                        <tr className="border-b border-[#ddd]">
                          <th className="p-0 min-h-[20px] bg-[#f5f5f5]">
                            <div className="h-full flex items-center">
                              <label className="px-3 py-3 w-[200px] inline-flex items-center gap-2 text-base text-[#7476A6]">
                                業種
                              </label>
                            </div>
                          </th>
                          <td className="p-0">
                            <div className="h-full flex items-center px-3 py-3 w-full ">
                              <ChrofyCustomSelect
                                lightNoSelect
                                options={optionsForSelect}
                                className="w-3/4 text-left"
                                style={{ textAlign: 'left' }}
                                value={industrySector}
                                onChange={(e) =>
                                  setIndustrySector(
                                    e.target.value ? e.target.value.replace('\u00A0\u00A0\u00A0', '') : ''
                                  )
                                }
                              />
                            </div>
                          </td>
                        </tr>

                        {/* 決算月の行 */}
                        <tr className="border-b border-[#ddd]">
                          <th className="p-0 min-h-[20px] bg-[#f5f5f5]">
                            <div className="h-full flex items-center">
                              <label className="px-3 py-3 w-[200px] inline-flex items-center gap-2 text-base text-[#7476A6]">
                                決算月
                              </label>
                            </div>
                          </th>

                          <td className="p-0">
                            <div className="h-full flex items-center px-3 py-3 w-full ">
                              <span className="text-left text-base">{accountingPeriod}</span>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="text-body-2-medium grow mt-6">■ 上場・開示情報</div>
                <div className="setting-table">
                  <table className="setting-table__table w-full form-table mt-4" style={{ borderCollapse: 'collapse' }}>
                    <colgroup>
                      <col style={{ width: '300px' }} />
                      <col />
                    </colgroup>

                    <tbody className="text-base">
                      <tr className="border-b border-[#ddd]">
                        <th className="p-0 min-h-[20px] bg-[#f5f5f5]">
                          <div className="h-full flex items-center">
                            <label className="px-3 py-3 w-[200px] inline-flex items-center gap-2 text-base text-[#7476A6]">
                              上場区分
                            </label>
                          </div>
                        </th>
                        <td className="p-0">
                          <div className="h-full flex items-center px-3 py-3 w-full ">
                            <ChrofyCustomSelect
                              lightNoSelect
                              options={listingClassificationOptions}
                              className="w-3/4 text-left"
                              style={{ textAlign: 'left' }}
                              value={listingClassificationType}
                              onChange={(e) => setlistingClassificationType(e.target.value ?? '')}
                            />
                          </div>
                        </td>
                      </tr>
                      <tr className="border-b border-[#ddd]">
                        <th className="p-0 min-h-[20px] bg-[#f5f5f5]">
                          <div className="h-full flex items-center">
                            <label className="px-3 py-3 w-[200px] inline-flex items-center gap-2 text-base text-[#7476A6]">
                              会社種類
                            </label>
                          </div>
                        </th>
                        <td className="p-0">
                          <div className="h-full flex items-center px-3 py-3 w-full ">
                            <ChrofyCustomSelect
                              lightNoSelect
                              options={reportingCompanyCategoryOptions}
                              className="w-3/4 text-left"
                              style={{ textAlign: 'left' }}
                              value={reportingCompanyCategoryType || ''}
                              onChange={(e) => setreportingCompanyCategoryType(e.target.value ?? '')}
                            />
                          </div>
                        </td>
                      </tr>
                      <tr className="border-b border-[#ddd]">
                        <th className="p-0 min-h-[20px] bg-[#f5f5f5]">
                          <div className="h-full flex items-center">
                            <label className="px-3 py-3 w-[200px] inline-flex items-center gap-2 text-base text-[#7476A6]">
                              市場分類
                            </label>
                          </div>
                        </th>
                        <td className="p-0">
                          <div className="h-full flex items-center px-3 py-3 w-full ">
                            <ChrofyCustomSelect
                              lightNoSelect
                              options={marketSegmentOptions}
                              className="w-3/4 text-left"
                              style={{ textAlign: 'left' }}
                              value={marketSegmentType}
                              onChange={(e) => setmarketSegmentType(e.target.value ?? '')}
                            />
                          </div>
                        </td>
                      </tr>

                      <tr className="border-b border-[#ddd]">
                        <th className="p-0 min-h-[20px] bg-[#f5f5f5]">
                          <div className="h-full flex items-center">
                            <label className="px-3 py-3 w-[200px] inline-flex items-center gap-2 text-base text-[#7476A6]">
                              証券コード
                            </label>
                          </div>
                        </th>
                        <td className="p-0">
                          <div className="h-full flex flex-col px-3 py-3 w-full ">
                            <input
                              type="text"
                              className={`w-3/4 py-3 px-3 rounded-xl ${errorStockCode ? 'is-error' : 'border border-gray-300'}`}
                              value={stockCode}
                              onChange={(e) => setstockCode(e.target.value ?? '')}
                              placeholder="証券コードを入力してください（半角数字4桁）"
                            />
                            {errorStockCode && (
                              <div className="text-red-600 mt-1 text-left self-start">{errorStockCode}</div>
                            )}
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="text-body-2-medium grow mt-6">■ その他管理情報</div>
                <div className="setting-table">
                  <table className="setting-table__table w-full form-table mt-4" style={{ borderCollapse: 'collapse' }}>
                    <colgroup>
                      <col style={{ width: '300px' }} />
                      <col />
                    </colgroup>

                    <tbody className="text-base">
                      <tr className="border-b border-[#ddd]">
                        <th className="p-0 min-h-[20px] bg-[#f5f5f5]">
                          <div className="h-full flex items-center">
                            <label className="px-3 py-3 w-[200px] inline-flex items-center gap-2 text-base text-[#7476A6]">
                              中核企業フラグ
                            </label>
                          </div>
                        </th>

                        <td className="p-0">
                          <div className="h-full flex items-center px-3 py-3 w-full">
                            <Checkbox
                              size="large"
                              checkedIcon={<CheckboxRountedIcon />}
                              checked={IsCoreCompany}
                              onChange={handleIsCoreCompanyChange}
                              inputProps={{ 'aria-label': '中核企業フラグ' }}
                            />
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <div className="content-footer mt-16 mb-8">
                    <div className="border-t border-blue1" />
                    <div className="footer_button_area mt-8 flex gap-4 justify-end">
                      <Link
                        color="inherit"
                        variant="button"
                        underline="none"
                        component={RouterLink}
                        to="/master/masterList"
                      >
                        <button type="button" className="footer-btn button_back">
                          キャンセル
                        </button>
                      </Link>
                      <button
                        className="btn footer-btn button_base"
                        onClick={() => updateCompanyInfo()}
                        disabled={!hasCompanyData}
                      >
                        登録
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              {/* 適用対象（EmploymentTypeInfo 同様、メインボックス外の独立カード） */}
              <RelatedDashboard
                key={`company-related-dashboard-${normalizeRelatedDashboardIds(master?.RelatedDashboard).join('|')}`}
                dashboards={normalizeRelatedDashboardIds(master?.RelatedDashboard)}
                authority={props.authority}
                mode={props.mode}
              />
            </div>
          </div>
        </section>
      </main>
    </div>
  )
}

/**
 * CompanyInfoコンポーネント
 *
 * ユーザーの権限に基づいて 会社情報設定ページを表示。 ユーザーが適切な権限を持っていない場合には、Forbiddenコンポーネントを表示。
 *
 * @returns {JSX.Element} 会社情報設定ページまたは、Forbiddenコンポーネント
 */
const CompanyInfo = () => {
  const state = useSelector((state) => state)
  const navigate = useNavigate()
  const showModal = useModal()

  // 権限を持っていない場合は画面表示しない
  if (
    !state.authority.Master_AuthInfo.Availability ||
    !state.authority.Master_AuthInfo.Auth_Conf.Company.CompanyInfo_Set
  ) {
    return <Forbidden />
  }

  return (
    <CompanyInfoContent
      pageTitle={titleName.companyInfo + titleName.common}
      corporationNumber={state.company.corporationNumber}
      corporationName={state.company.name}
      bucketName={'chrofy-' + process.env.REACT_APP_ENV + '-' + state.company.corporationNumber}
      accPeriod={state.company.accPeriod}
      userId={state.user.sub}
      userName={state.user.userName}
      authority={state.authority}
      mode={state.analysis.mode}
      navigate={navigate}
      showModal={showModal}
    />
  )
}

export default CompanyInfo
